import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  DollarSign,
  Workflow,
  BrainCircuit,
  Lock,
  Search,
  GitBranch,
  Server,
  CheckCircle,
  UploadCloud,
  CloudCog,
  BarChart3,
  Headphones
} from 'lucide-react';

const Services = () => {
  const navigate = useNavigate();

  // Tier 1 & 2: Core Intelligence Layer Services
  const coreServices = [
    {
      tier: 'pre-engineered',
      badge: 'Ready to Deploy',
      badgeColor: 'primary',
      icon: DollarSign,
      title: 'Financial Intelligence',
      headline: 'Your Ledger. Always in Context.',
      description: 'Automated financial synthesis connecting your point of sale, accounting, scheduling, and banking systems. Get real-time margin visibility, automated reconciliation, and "chat-with-your-ledger" capabilities—without surrendering your data.',
      features: [
        'Cross-platform reconciliation',
        'Real-time margin analysis',
        'Natural language financial queries',
        'Automated expense categorization'
      ],
      trustNote: 'Read-only API access · Zero data retention',
      route: '/finance-intelligence',
      ctaText: 'View Finance Agent'
    },
    {
      tier: 'pre-engineered',
      badge: 'Ready to Deploy',
      badgeColor: 'primary',
      icon: Workflow,
      title: 'Operational Triage & Intelligence',
      headline: '60-Second Technical Fact Sheets.',
      description: 'AI-powered triage and documentation synthesis connecting ticketing systems, knowledge bases, and real-time system status. Generate instant technical context and best-effort triage—reducing Level 1 noise by up to 40%.',
      features: [
        'Automated knowledge synthesis',
        'Instant technical fact sheets',
        'Ticket triage recommendations',
        'Real-time system status queries'
      ],
      trustNote: 'Read-only access · Client-controlled AI',
      route: '/operational-intelligence',
      ctaText: 'View Triage Agent'
    },
    {
      tier: 'bespoke',
      badge: 'Custom Build',
      badgeColor: 'amber',
      icon: BrainCircuit,
      title: 'Bespoke Intelligence',
      headline: 'If It Has an API, We Can Build Its Brain.',
      description: 'Custom AI intelligence for specialized industries and unique datasets. We design custom workflows and synthesis prompts tailored to your exact business logic—then deploy with your chosen AI model, integrated directly into your infrastructure so your data remains yours.',
      features: [
        'Legacy system integration',
        'Industry-specific workflows',
        'Custom data synthesis',
        'Proprietary software connections'
      ],
      trustNote: 'Sovereign deployment · Full ownership',
      route: '/bespoke-orchestration',
      ctaText: 'Learn More'
    }
  ];

  // Infrastructure & Maintenance Services
  const infrastructureServices = [
    {
      icon: UploadCloud,
      title: 'Host Your Intelligence Layer',
      description: 'Secure, scalable infrastructure for client-controlled AI workloads and data pipelines.'
    },
    {
      icon: CloudCog,
      title: 'Protect Your AI Infrastructure',
      description: 'End-to-end security and maintenance for your AI infrastructure.'
    },
    {
      icon: BarChart3,
      title: 'Monitor Your Data Pipelines',
      description: 'Proactive monitoring ensuring continuous availability of your business intelligence.'
    },
    {
      icon: Headphones,
      title: 'Ongoing AI Workflow Support',
      description: 'Expert support for context orchestration, AI model optimization, and system tuning.'
    }
  ];

  // Integrator Methodology Steps
  const methodologySteps = [
    {
      step: '01',
      icon: Search,
      title: 'Data Audit',
      subtitle: 'Read-Only API Review',
      description: 'We map your data landscape with read-only access. No writes. No downloads. Just discovery.',
      trustElement: '🔒 Read-Only · Zero Data Retention'
    },
    {
      step: '02',
      icon: GitBranch,
      title: 'Logic Mapping',
      subtitle: 'Context Orchestration Design',
      description: 'We architect the data flows and synthesis prompts that transform scattered information into actionable intelligence.',
      trustElement: '🔧 Custom Workflows · Your Business Logic'
    },
    {
      step: '03',
      icon: Server,
      title: 'Client-Controlled Deployment',
      subtitle: 'Your AI, Your Control',
      description: 'Deploy with your chosen AI model: Self-hosted or cloud-based using your API keys. Your data never leaves your control. We build it, you own it.',
      trustElement: '🛡️ Your AI Choice · Complete Ownership'
    }
  ];

  const handleServiceClick = (service) => {
    if (service.scrollToContact) {
      const element = document.getElementById('contact');
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
        // Pre-fill contact form
        setTimeout(() => {
          const subjectField = document.querySelector('select[name="subject"]');
          if (subjectField) {
            subjectField.value = 'Custom Orchestration Audit';
          }
        }, 100);
      }
    } else {
      navigate(service.route);
      window.scrollTo(0, 0);
    }
  };

  return (
    <section id="services" className="pt-12 pb-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

        {/* Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">
            Proven AI Workflows. Built for Your Business.
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            From pre-engineered integrations to custom orchestrations—we deliver the Intelligence Layer
            your business needs, at the speed it demands.
          </p>
        </div>

        {/* Core Services Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-20">
          {coreServices.map((service, index) => {
            const IconComponent = service.icon;
            const badgeColorClass = service.badgeColor === 'primary'
              ? 'bg-primary-600 text-white'
              : 'bg-amber-500 text-white';

            return (
              <div
                key={index}
                className="h-full flex flex-col group bg-gray-50 hover:bg-white p-8 rounded-2xl border border-gray-100 hover:border-primary-200 hover:shadow-xl transition-all duration-300 relative"
              >
                <div className="flex-1">
                  {/* Badge */}
                  <div className={`absolute top-6 right-6 ${badgeColorClass} text-xs font-bold px-3 py-1 rounded-full`}>
                    {service.badge}
                  </div>

                  {/* Icon */}
                  <div className="flex items-center justify-center w-16 h-16 bg-primary-100 group-hover:bg-primary-600 rounded-xl mb-6 transition-colors duration-300">
                    <IconComponent className="h-8 w-8 text-primary-600 group-hover:text-white transition-colors duration-300" />
                  </div>

                  {/* Title */}
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">
                    {service.title}
                  </h3>

                  {/* Headline */}
                  <p className="text-lg font-medium text-primary-600 mb-4">
                    {service.headline}
                  </p>

                  {/* Description */}
                  <p className="text-gray-600 leading-relaxed mb-6">
                    {service.description}
                  </p>

                  {/* Features */}
                  <ul className="space-y-2 mb-6">
                    {service.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-sm text-gray-600">
                        <CheckCircle className="h-4 w-4 text-primary-600 mt-0.5 flex-shrink-0" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>

                  {/* Trust Note */}
                  <div className="flex items-center gap-2 text-sm text-gray-500 mb-6 pt-4 border-t border-gray-200">
                    <Lock className="h-4 w-4 flex-shrink-0" />
                    <span>{service.trustNote}</span>
                  </div>
                </div>

                {/* CTA */}
                <button
                  onClick={() => handleServiceClick(service)}
                  className="w-full bg-primary-600 hover:bg-primary-700 text-white py-3 px-6 rounded-lg font-semibold transition-all duration-200 group-hover:shadow-lg"
                >
                  {service.ctaText} →
                </button>
              </div>
            );
          })}
        </div>

        {/* Integrator Methodology Section */}
        <div className="bg-gradient-to-br from-primary-50 to-white rounded-3xl p-12 mb-20">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Our Intelligent Method
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Our three-step process ensures your Intelligence Layer is built right—with privacy,
              ownership, and your business logic at the center.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {methodologySteps.map((step, index) => {
              const IconComponent = step.icon;
              return (
                <div key={index} className="relative">
                  {/* Connecting Arrow (hidden on mobile) */}
                  {index < methodologySteps.length - 1 && (
                    <div className="hidden md:block absolute top-24 -right-4 text-primary-300 text-4xl">
                      →
                    </div>
                  )}

                  <div className="bg-white p-8 rounded-2xl shadow-lg border border-gray-100 text-center h-full">
                    {/* Icon */}
                    <div className="bg-primary-100 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4">
                      <IconComponent className="h-10 w-10 text-primary-600" />
                    </div>

                    {/* Step Badge */}
                    <div className="bg-primary-600 text-white text-xs font-bold px-3 py-1 rounded-full inline-block mb-4">
                      STEP {step.step}
                    </div>

                    {/* Title */}
                    <h3 className="text-2xl font-bold text-gray-900 mb-2">
                      {step.title}
                    </h3>

                    {/* Subtitle */}
                    <p className="text-sm font-semibold text-primary-600 mb-4">
                      {step.subtitle}
                    </p>

                    {/* Description */}
                    <p className="text-gray-600 mb-4 leading-relaxed">
                      {step.description}
                    </p>

                    {/* Trust Element */}
                    <div className="text-sm text-primary-600 font-medium">
                      {step.trustElement}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Infrastructure & Maintenance Services */}
        <div className="mb-16">
          <h3 className="text-2xl font-bold text-gray-900 text-center mb-8">
            Infrastructure & Maintenance
          </h3>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {infrastructureServices.map((service, index) => {
              const IconComponent = service.icon;
              return (
                <div
                  key={index}
                  className="bg-gray-50 p-6 rounded-xl border border-gray-100 hover:border-primary-200 hover:shadow-lg transition-all duration-300"
                >
                  <div className="flex items-center justify-center w-12 h-12 bg-primary-100 rounded-lg mb-4">
                    <IconComponent className="h-6 w-6 text-primary-600" />
                  </div>
                  <h4 className="text-lg font-semibold text-gray-900 mb-2">
                    {service.title}
                  </h4>
                  <p className="text-sm text-gray-600 leading-relaxed">
                    {service.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>

        {/* CTA Section */}
        <div className="text-center bg-primary-50 rounded-2xl p-12">
          <h3 className="text-2xl font-bold text-gray-900 mb-4">
            Ready to Build Your Intelligence Layer?
          </h3>
          <p className="text-gray-600 mb-8 max-w-2xl mx-auto">
            Let's discuss how we can connect your data sources into a unified AI-powered system
            that you own and control—your AI choice, no compromises.
          </p>
          <button
            onClick={() => {
              const element = document.getElementById('contact');
              if (element) element.scrollIntoView({ behavior: 'smooth' });
            }}
            className="bg-primary-600 hover:bg-primary-700 text-white px-8 py-4 rounded-lg font-semibold text-lg transition-all duration-200 shadow-lg hover:shadow-xl"
          >
            Request a Workflow Audit
          </button>
        </div>
      </div>
    </section>
  );
};

export default Services;
